import { useEffect, useState } from 'react';
import { Info, Truck, Wrench } from 'shared/icons';
import api from '../../../services/api';
import Input from '../../ui/Input';
import { MONO_CLASS } from '../../ui/Mono';
import Select from '../../ui/Select';
import { ExtractListRows } from '../payload';
import { ListProductionOrders } from '../production/productionApi';
import {
    OPEN_OUTBOUND_STATUSES,
    PICK_DESTINATION_PRODUCTION_ORDER,
    PICK_DESTINATION_SHIPMENT,
} from './repackModel';

/**
 * O DESTINO DO PICKING FRACIONADO — a tela que precisa dizer, antes de tudo,
 * uma coisa que engana muita gente: **o fracionado NÃO gera pallet**.
 *
 * ── Por que o destino é obrigatório, e como isso aparece ────────────────────
 * O fracionado tira saldo do pallet de origem e credita em UM de dois lugares
 * (§3, invariante de saldo): o consumo de uma OP (a bancada do M6) ou um volume
 * de expedição de um romaneio. Sem um dos dois, o saldo sairia do sistema — e é
 * exatamente isso que o backend recusa (409
 * `WMS26_REPACK_PICK_WITHOUT_DESTINATION`).
 *
 * A tela trata essa recusa pelo que ela é: **falta escolher o destino**, dito
 * ANTES de tentar (o botão de confirmar explica o que falta) e repetido com as
 * mesmas palavras se a recusa vier do servidor. Ninguém precisa descobrir na
 * marra que "erro 409" queria dizer "você não escolheu para onde vai".
 *
 * ── Degradação honesta de permissão ─────────────────────────────────────────
 * Listar OPs exige `wms:manage:production` (ou `wms:operate`) e listar
 * romaneios exige o módulo de expedição — quem tem só `wms:manage:repack` não
 * enxerga nenhum dos dois. Em vez de travar a operação, o select vira CAMPO
 * LIVRE de ID e a tela diz por quê (o mesmo caminho que o recebimento do M3 usa
 * com as ordens de compra).
 */

const PRODUCTION_ORDER_LIMIT = 100;
const OUTBOUND_LIMIT = 100;

/** Carrega uma lista opcional: 403 não é erro, é "sem acesso a esta lista". */
function useOptionalList(loader, listKeys, enabled) {
    const [rows, setRows] = useState([]);
    const [unavailable, setUnavailable] = useState(false);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        let alive = true;
        loader()
            .then((data) => {
                if (alive) {
                    setRows(ExtractListRows(data, listKeys));
                    setUnavailable(false);
                }
            })
            .catch(() => {
                if (alive) {
                    setRows([]);
                    setUnavailable(true);
                }
            });
        return () => {
            alive = false;
        };
    }, [loader, listKeys, enabled]);

    return { rows, unavailable };
}

const PRODUCTION_ORDER_KEYS = ['results', 'production_orders'];
const OUTBOUND_KEYS = ['results', 'outbounds'];
const LOAD_PRODUCTION_ORDERS = () => ListProductionOrders({ limit: PRODUCTION_ORDER_LIMIT });
const LOAD_OUTBOUNDS = () => api.getOutbounds({ limit: OUTBOUND_LIMIT, offset: 0 });

const ChoiceCard = ({ checked, onSelect, Icon, title, hint, children, disabled }) => (
    <div
        className={`space-y-3 rounded-xl border-2 p-4 transition-colors ${
            checked
                ? 'border-black bg-mono-50 dark:border-white dark:bg-mono-900/50'
                : 'border-mono-200 dark:border-mono-800'
        }`}
    >
        <label className="flex items-start gap-2">
            <input type="radio" name="pick-destination" checked={checked} onChange={onSelect} disabled={disabled} />
            <span className="min-w-0">
                <span className="flex items-center gap-2 text-sm font-bold text-black dark:text-white">
                    <Icon size={16} className="shrink-0 text-mono-500" />
                    {title}
                </span>
                <span className="mt-0.5 block text-xs text-mono-500 dark:text-mono-400">{hint}</span>
            </span>
        </label>
        {checked ? <div className="space-y-3 pl-6">{children}</div> : null}
    </div>
);

const PickDestinationEditor = ({ label, destination, onChange, disabled = false }) => {
    const isProductionOrder = destination.kind === PICK_DESTINATION_PRODUCTION_ORDER;

    const productionOrders = useOptionalList(LOAD_PRODUCTION_ORDERS, PRODUCTION_ORDER_KEYS, isProductionOrder);
    const outbounds = useOptionalList(LOAD_OUTBOUNDS, OUTBOUND_KEYS, !isProductionOrder);

    const openOutbounds = outbounds.rows.filter((row) =>
        OPEN_OUTBOUND_STATUSES.includes(String(row?.status || '').toLowerCase()),
    );

    return (
        <section className="space-y-3">
            {/* A regra que mais engana, dita antes do formulário. */}
            <div className="flex items-start gap-2 rounded-xl border border-info-400 bg-info-soft px-3 py-3 dark:border-info-700 dark:bg-info-900/25">
                <Info size={18} className="mt-0.5 shrink-0 text-info-text dark:text-info-300" />
                <div>
                    <p className="text-sm font-bold text-info-text dark:text-info-200">
                        {label('wms.repack.pick_no_lpn_title')}
                    </p>
                    <p className="text-xs text-info-text dark:text-info-300">{label('wms.repack.pick_no_lpn_body')}</p>
                </div>
            </div>

            <h3 className="text-sm font-bold text-black dark:text-white">
                {label('wms.repack.pick_destination_title')}
            </h3>

            <div className="grid gap-3 md:grid-cols-2">
                <ChoiceCard
                    checked={isProductionOrder}
                    onSelect={() => onChange({ kind: PICK_DESTINATION_PRODUCTION_ORDER })}
                    Icon={Wrench}
                    title={label('wms.repack.pick_op')}
                    hint={label('wms.repack.pick_op_hint')}
                    disabled={disabled}
                >
                    {productionOrders.unavailable ? (
                        <>
                            <p className="text-xs text-mono-500 dark:text-mono-400">
                                {label('wms.repack.pick_op_unavailable')}
                            </p>
                            <Input
                                label={label('wms.repack.pick_op_id')}
                                value={destination.productionOrderId}
                                onChange={(event) => onChange({ productionOrderId: event.target.value })}
                                autoComplete="off"
                                disabled={disabled}
                                className={MONO_CLASS}
                            />
                        </>
                    ) : (
                        <Select
                            label={label('wms.repack.pick_op_field')}
                            value={destination.productionOrderId}
                            onChange={(event) => onChange({ productionOrderId: event.target.value })}
                            disabled={disabled}
                            className={MONO_CLASS}
                        >
                            <option value="">—</option>
                            {productionOrders.rows.length === 0 ? (
                                <option value="" disabled>
                                    {label('wms.repack.pick_op_empty')}
                                </option>
                            ) : null}
                            {productionOrders.rows.map((row) => (
                                <option key={row.id} value={String(row.id)}>
                                    {`${row.code} · ${row.status}${row.product_out_sku ? ` · ${row.product_out_sku}` : ''}`}
                                </option>
                            ))}
                        </Select>
                    )}
                </ChoiceCard>

                <ChoiceCard
                    checked={!isProductionOrder}
                    onSelect={() => onChange({ kind: PICK_DESTINATION_SHIPMENT })}
                    Icon={Truck}
                    title={label('wms.repack.pick_shipment')}
                    hint={label('wms.repack.pick_shipment_hint')}
                    disabled={disabled}
                >
                    {outbounds.unavailable ? (
                        <>
                            <p className="text-xs text-mono-500 dark:text-mono-400">
                                {label('wms.repack.pick_outbound_unavailable')}
                            </p>
                            <Input
                                label={label('wms.repack.pick_outbound_id')}
                                value={destination.outboundId}
                                onChange={(event) => onChange({ outboundId: event.target.value })}
                                autoComplete="off"
                                disabled={disabled}
                                className={MONO_CLASS}
                            />
                        </>
                    ) : (
                        <Select
                            label={label('wms.repack.pick_outbound_field')}
                            value={destination.outboundId}
                            onChange={(event) => onChange({ outboundId: event.target.value })}
                            disabled={disabled}
                        >
                            <option value="">—</option>
                            {openOutbounds.length === 0 ? (
                                <option value="" disabled>
                                    {label('wms.repack.pick_outbound_empty')}
                                </option>
                            ) : null}
                            {openOutbounds.map((row) => (
                                <option key={row.id} value={String(row.id)}>
                                    {`${row.freight_code ? `#${row.freight_code}` : row.id} · ${row.status}${
                                        row.haulier_name ? ` · ${row.haulier_name}` : ''
                                    }`}
                                </option>
                            ))}
                        </Select>
                    )}
                    <Input
                        label={label('wms.repack.pick_volume_code')}
                        value={destination.volumeCode}
                        onChange={(event) => onChange({ volumeCode: event.target.value.toUpperCase() })}
                        placeholder="NBA1234567890"
                        autoComplete="off"
                        disabled={disabled}
                        className={MONO_CLASS}
                    />
                    <p className="text-xs text-mono-500 dark:text-mono-400">
                        {label('wms.repack.pick_volume_hint')}
                    </p>
                </ChoiceCard>
            </div>
        </section>
    );
};

export default PickDestinationEditor;
